import {
  Avatar,
  AvatarFallback,
  cn,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@beweegbaas/ui";
import { Link, useLocation } from "@tanstack/react-router";
import type { ComponentProps, ComponentType, SVGProps } from "react";
import { AdminSidebarBanner } from "#/modules/admin/components/admin-banners";
import { useHasReadLatestReleaseNotes, useUser } from "#/modules/util.auth/use-user";
import { useLocaleParams } from "#/modules/util.helpers/route-href";
import { useTheme } from "#/modules/util.theme/theme";
import { m } from "#/paraglide/messages";

interface AdminNavItem {
  label: string;
  match: string;
  to: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

const menuItems: AdminNavItem[] = [
  {
    label: "Dashboard",
    match: "/admin/dashboard",
    to: "/$locale/{-$schoolyear}/admin/dashboard",
    icon: IconMaterialSymbolsDashboardOutline,
  },
  {
    label: "Beweegactiviteiten",
    match: "/admin/activities",
    to: "/$locale/{-$schoolyear}/admin/activities",
    icon: IconMaterialSymbolsSportsHandball,
  },
  {
    label: "Sociaal-emotioneel",
    match: "/admin/social-emotional",
    to: "/$locale/{-$schoolyear}/admin/social-emotional",
    icon: IconMaterialSymbolsSentimentSatisfiedOutline,
  },
  {
    label: "Sporten",
    match: "/admin/sports",
    to: "/$locale/{-$schoolyear}/admin/sports",
    icon: IconMaterialSymbolsExercise,
  },
  {
    label: "Gebruikers",
    match: "/admin/users",
    to: "/$locale/{-$schoolyear}/admin/users",
    icon: IconMaterialSymbolsPersonOutline,
  },
  {
    label: "Scholen",
    match: "/admin/schools",
    to: "/$locale/{-$schoolyear}/admin/schools",
    icon: IconMaterialSymbolsSchoolOutline,
  },
  {
    label: "Queue",
    match: "/admin/queue",
    to: "/$locale/{-$schoolyear}/admin/queue",
    icon: IconMaterialSymbolsChecklist,
  },
  {
    label: "Tickets",
    match: "/admin/tickets",
    to: "/$locale/{-$schoolyear}/admin/tickets",
    icon: IconMaterialSymbolsMailOutline,
  },
  {
    label: "Melding",
    match: "/admin/system-notification",
    to: "/$locale/{-$schoolyear}/admin/system-notification",
    icon: IconMaterialSymbolsCampaignOutline,
  },
];

/** Matches the "Tickets" entry above — the only nav item that carries an unread badge. */
const TICKETS_NAV_TO = "/$locale/{-$schoolyear}/admin/tickets";

/**
 * Label of the admin nav item that owns the current URL. Used as the mobile page-title
 * fallback next to the sidebar trigger (`Page.Header`) on admin pages without an explicit title.
 */
export function useActiveAdminNavLabel(): string | undefined {
  const { pathname } = useLocation();
  return menuItems.find((item) => pathname.includes(item.match))?.label;
}

function AdminNavUser() {
  const user = useUser();
  const localeLinkParams = useLocaleParams();
  const { setOpenMobile, isMobile } = useSidebar();
  const { enabled: darkModeEnabled, isDark, toggleDark } = useTheme();
  const hasReadLatestReleaseNotes = useHasReadLatestReleaseNotes();

  if (!user) return null;

  const initials =
    [user.firstName?.[0], user.lastName?.[0]].filter(Boolean).join("").toUpperCase() ||
    user.email?.[0]?.toUpperCase() ||
    "?";
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  const fullName = name || user.email;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton
                size="lg"
                aria-label={m["common.aria.open_menu"]()}
                className="data-open:bg-sidebar-accent border data-open:text-sidebar-accent-foreground"
              />
            }
          >
            <Avatar>
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{fullName}</span>
              {name && <span className="truncate text-xs">{user.email}</span>}
            </div>
            <IconMaterialSymbolsExpandAll className="ml-auto" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
            className="w-fit min-w-56"
          >
            <DropdownMenuGroup>
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar>
                    <AvatarFallback>{initials}</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{fullName}</span>
                    {name && <span className="truncate text-xs">{user.email}</span>}
                  </div>
                </div>
              </DropdownMenuLabel>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                render={
                  <Link
                    to="/$locale/{-$schoolyear}/admin/account-settings/account-info"
                    params={localeLinkParams}
                    onClick={() => setOpenMobile(false)}
                  />
                }
              >
                <IconMaterialSymbolsSettings />
                {m["account.account_settings"]()}
              </DropdownMenuItem>
              <DropdownMenuItem
                render={
                  <Link
                    to="/$locale/{-$schoolyear}/admin/release-notes"
                    params={localeLinkParams}
                    onClick={() => setOpenMobile(false)}
                  />
                }
              >
                <span className="relative inline-flex items-center gap-2">
                  <IconMaterialSymbolsNewReleases />
                  {m["release_notes.title"]()}
                  {!hasReadLatestReleaseNotes && (
                    <span
                      data-testid="release-notes-unread-dot"
                      className="absolute -top-1 -right-4 inline-block size-3 rounded-full
                        bg-linear-to-br from-red-600 to-red-800"
                    />
                  )}
                </span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            {darkModeEnabled && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  {/* Two-state toggle over three underlying states — unchecking back to the OS
                      preference clears the override (see util.theme/theme.tsx). */}
                  <DropdownMenuCheckboxItem checked={isDark} onCheckedChange={toggleDark}>
                    <IconMaterialSymbolsDarkMode />
                    {m["common.theme.dark_mode"]()}
                  </DropdownMenuCheckboxItem>
                </DropdownMenuGroup>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                render={<Link to="/$locale/{-$schoolyear}/logout" params={localeLinkParams} />}
              >
                <IconMaterialSymbolsLogout />
                {m["common.actions.logout"]()}
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

/** Admin chrome as a sidebar — replaces the old `AdminHeader` top nav. */
export function AdminSidebar({
  className,
  unreadTicketCount = 0,
  ...props
}: ComponentProps<typeof Sidebar> & { unreadTicketCount?: number }) {
  const localeLinkParams = useLocaleParams();
  const { pathname } = useLocation();
  const { setOpenMobile } = useSidebar();
  return (
    <Sidebar
      variant="inset"
      collapsible="icon"
      // Transparent rail — see AppSidebar: the page-background gradient is the backdrop.
      className={cn("[&_[data-slot=sidebar-inner]]:bg-transparent", className)}
      {...props}
    >
      <SidebarHeader>
        <AdminSidebarBanner always />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Beheer</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => {
                const showUnreadDot = item.to === TICKETS_NAV_TO && unreadTicketCount > 0;
                return (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton
                      isActive={pathname.includes(item.match)}
                      tooltip={item.label}
                      render={
                        <Link
                          to={item.to}
                          params={localeLinkParams}
                          onClick={() => setOpenMobile(false)}
                        />
                      }
                    >
                      <item.icon />
                      <span className="relative inline-flex items-center gap-2">
                        {item.label}
                        {showUnreadDot && (
                          <span
                            data-testid="admin-tickets-unread-dot"
                            className="absolute -top-1 -right-4 inline-block size-3
                              rounded-full bg-linear-to-br from-red-600 to-red-800
                              group-data-[collapsible=icon]:hidden"
                          />
                        )}
                      </span>
                    </SidebarMenuButton>
                    {/* Collapsed-rail fallback: SidebarMenuButton clips overflow at its 32px
                        icon-square size, so the dot above disappears when collapsed. This sibling
                        lives outside the button, anchored to the (relatively positioned)
                        SidebarMenuItem instead — same "collapsed-only sibling" technique as
                        AppSidebar's SubscriptionMenuItem. */}
                    {showUnreadDot && (
                      <span
                        data-testid="admin-tickets-unread-dot-collapsed"
                        className="absolute top-0.5 right-0.5 hidden size-2 rounded-full
                          bg-linear-to-br from-red-600 to-red-800
                          group-data-[collapsible=icon]:block"
                      />
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <AdminNavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
